import { Injectable } from '@angular/core';
import { BehaviorSubject, catchError, of, take } from 'rxjs';
import { LanguagesEnum } from '../../common-panel/enums/languages-enum';
import { SendindStatesEnum } from '../common/enums/sendind-states-enum';
import { UserRolesEnum } from '../common/enums/user-roles-enum';
import { UserStatusesEnum } from '../common/enums/user-statuses-enum';
import { UserListItemJSON } from '../common/models/server-response/user-list-item-json.model';
import { UserListItem } from '../common/models/users-data/user-list-item.model';
import { SortingHeadersEnum } from '../sections/home-page/home-table/enums/sorting-headers-enum';
import { RequestsService } from './requests.service';
import { UserRightRolesEnum } from '../common/enums/user-right-roles-enum';
import { SortingDirectionsEnum } from 'src/common-panel/enums/sorting-directions-enum';
import { ServerValueInterpreterService } from 'src/common-panel/services/server-value-interpreter.service';
import { COUNTRIES_IDS, CountryId } from 'src/common-panel/constants/country-configs.constants';
@Injectable({
  providedIn: 'root'
})
export class UserListService
{
  data: UserListItem[] = []
  loadedUserListSubject: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false)

  filterTableByUserName: BehaviorSubject<string> = new BehaviorSubject<string>('')
  filterTableByEmail: BehaviorSubject<string> = new BehaviorSubject<string>('')
  filterTableByClinicId: BehaviorSubject<string> = new BehaviorSubject<string>('')
  clearedFilters: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true)
  needsToRefresh: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false)

  tableSettings: {
    sortingHeader: SortingHeadersEnum;
    sortDirection: SortingDirectionsEnum;
    pageSize: number;
    pageIndex: number;
    roleFilterState: UserRolesEnum | undefined;
    countryFilterState: CountryId | undefined;
  } = {
      sortingHeader: SortingHeadersEnum.name,
      sortDirection: SortingDirectionsEnum.none,
      pageSize: 20,
      pageIndex: 0,
      roleFilterState: undefined,
      countryFilterState: undefined
    }

  constructor(private requestsService: RequestsService, private serverValueInterpreterService: ServerValueInterpreterService) { }

  loadData(): BehaviorSubject<boolean>
  {
    this.loadedUserListSubject.next(false)
    this.requestsService.getUserList().pipe(
      take(1),
      catchError(err => { return of([]) })
    )
      .subscribe(l_users =>
      {
        let l_data: UserListItem[] = []

        let l_ids = l_users.map(val => val.id)

        this.requestsService.getClinicLists(l_ids).pipe(take(1)).subscribe(l_objects =>
        {
          l_users.forEach(l_user =>
          {
            if(!Object.values(COUNTRIES_IDS).includes(l_user.country) ) return;
            let l_object = l_objects.filter(val => val.userId == l_user.id)
            if (l_object.length > 0)
            {
              let l_checkedUser = this.checkUserData(l_user, l_object[0].objectList)
              if (l_checkedUser)
              {
                l_data.push(l_checkedUser)
              }
            } else
            {
              let l_checkedUser = this.checkUserData(l_user, [])
              if (l_checkedUser)
              {
                l_data.push(l_checkedUser)
              }
            }


          })
          this.data = l_data
          this.loadedUserListSubject.next(true)
        })

      })

    return this.loadedUserListSubject
  }

  checkUserData(userListItem: UserListItemJSON, clinicList: string[]): UserListItem | null
  {

    if (!userListItem.id)
    {
      console.error(`Error: User ${ JSON.stringify(userListItem) } hasn't ID.`)

      return null;
    }

    if (!userListItem.email)
    {
      console.error(`Error: User ${ JSON.stringify(userListItem) } - hasn't E-mail.`)
      return null;
    }

    let l_error: string[] = []
    let l_role
    if (Object.values(UserRolesEnum).includes(userListItem.customAttributes.Role as UserRolesEnum) && (userListItem.customAttributes.Role != UserRolesEnum.none))
    {
      l_role = userListItem.customAttributes.Role as UserRolesEnum
    } else
    {
      console.error(`Error: User ${ JSON.stringify(userListItem) } - hasn't role, that matches user roles: ${ Object.values(UserRolesEnum) }`)
      return null;
    }

    let l_statusDate: Date | null = new Date(userListItem.customAttributes.StatusInSystemDate)
    if (isNaN(l_statusDate.getTime()))
    {
      l_statusDate = null;
      l_error.push(`- status date isn't valid`)
    }

    let l_status
    if (userListItem.customAttributes.InvitationSent && userListItem.customAttributes.InvitationAccepted)
    {
      l_status = userListItem.accountEnabled ? UserStatusesEnum.active : UserStatusesEnum.inactive
    } else if (userListItem.customAttributes.InvitationSent && !userListItem.customAttributes.InvitationAccepted)
    {
      l_status = UserStatusesEnum.invited
    } else
    {
      l_status = userListItem.accountEnabled ? UserStatusesEnum.active : UserStatusesEnum.inactive
      l_error.push("- InvitationSent is null")
    }

    let l_lang
    if (userListItem.customAttributes.Language && Object.values(LanguagesEnum).includes(userListItem.customAttributes.Language as LanguagesEnum))
    {
      l_lang = userListItem.customAttributes.Language as LanguagesEnum
    } else
    {
      l_error.push(`- hasn't lang, that matches possible languages: ${ Object.values(LanguagesEnum) }`)
    }


    let l_countryId
    let l_country

    if (Object.values(COUNTRIES_IDS).includes(userListItem.country))
    {
      l_countryId = userListItem.country
      l_country = this.serverValueInterpreterService.getFullCountryNameById(userListItem.country)
    } else
    {
      l_error.push(`- hasn't country id, that matches possible countries: ${ Object.values(COUNTRIES_IDS) }`)
    }

    if (l_error.length > 0)
    {
      console.error(` Error in user ${ JSON.stringify(userListItem) }:\n` + l_error.join(';\n') + '.')
    }


    let l_userListItem: UserListItem = {
      userId: userListItem.id,
      firstName: userListItem.firstName ? userListItem.firstName : '',
      lastName: userListItem.lastName ? userListItem.lastName : '',
      email: userListItem.email,
      role: l_role,
      status: l_status,
      statusDate: l_statusDate,
      lang: l_lang,
      country: l_country,
      countryId: l_countryId,
      shopList: clinicList,
      clinicId: userListItem.customAttributes.ShopId,
      entOfficeId: userListItem.customAttributes.ENTOfficeId,
      invitationProgress$: new BehaviorSubject<SendindStatesEnum>(SendindStatesEnum.none),
      shopAdminAccess: userListItem.customAttributes.ExtendedRoles ? userListItem.customAttributes.ExtendedRoles.split(',').includes(UserRightRolesEnum.ShopAdmin) : false,
      countryShopsAccess: userListItem.customAttributes.ExtendedRoles ? userListItem.customAttributes.ExtendedRoles.split(',').includes(UserRightRolesEnum.CountryManager) : false,
      patientManagerAccess: userListItem.customAttributes.ExtendedRoles ? userListItem.customAttributes.ExtendedRoles.split(',').includes(UserRightRolesEnum.PatientManager) : false,
      advancedMode:userListItem.customAttributes.ExtendedRoles ? userListItem.customAttributes.ExtendedRoles.split(',').includes(UserRightRolesEnum.AdvancedMode) : false,
    }
    return l_userListItem;
  }

  sendInvitation(userId: string)
  {
    this.data.find(el => el.userId == userId)?.invitationProgress$?.next(SendindStatesEnum.sending)
    this.requestsService.sendInvitation(userId).pipe(take(1)).subscribe(() =>
    {
      this.data.find(el => el.userId == userId)?.invitationProgress$?.next(SendindStatesEnum.sent)
    })
  }


}
